import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Coins, TrendingUp, TrendingDown, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const fmt = (n) => `${Number(n || 0).toLocaleString("fr-FR")} GNF`;

export default function ExploitationPage() {
  const today = new Date();
  const firstOfYear = `${today.getFullYear()}-01-01`;
  const todayStr = today.toISOString().slice(0, 10);
  const [from, setFrom] = useState(firstOfYear);
  const [to, setTo] = useState(todayStr);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/accounting/income-statement", { params: { date_from: from, date_to: to } });
      setData(data);
    } catch (e) {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const isProfit = (data?.resultat_net || 0) >= 0;

  return (
    <div className="space-y-6" data-testid="exploitation-page">
      <div>
        <div className="chip mb-2"><Coins className="h-3 w-3" /><span>COMPTE D&apos;EXPLOITATION</span></div>
        <h1 className="font-heading font-extrabold text-3xl text-[#1E3A8A]">Compte d&apos;exploitation</h1>
        <p className="text-slate-500 mt-1">Produits − Charges = Résultat net de l&apos;exercice (P&L SYSCOHADA).</p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
          <div><Label>Du</Label><Input type="date" value={from} onChange={e => setFrom(e.target.value)} data-testid="exp-from" /></div>
          <div><Label>Au</Label><Input type="date" value={to} onChange={e => setTo(e.target.value)} data-testid="exp-to" /></div>
          <Button onClick={load} disabled={loading} className="bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white" data-testid="exp-refresh"><RefreshCw className="h-4 w-4 mr-2" />Calculer</Button>
        </div>
      </div>

      {data && (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-green-50 border border-green-100 rounded-2xl p-5">
              <div className="flex items-center justify-between mb-2"><span className="text-xs uppercase tracking-wider text-slate-500 font-bold">Produits</span><TrendingUp className="h-5 w-5 text-[#166534]" /></div>
              <div className="text-2xl font-extrabold text-[#166534] tabular-nums">{fmt(data.total_produits)}</div>
              <div className="text-xs text-slate-500 mt-1">{data.produits.length} compte(s) mouvementé(s)</div>
            </div>
            <div className="bg-orange-50 border border-orange-100 rounded-2xl p-5">
              <div className="flex items-center justify-between mb-2"><span className="text-xs uppercase tracking-wider text-slate-500 font-bold">Charges</span><TrendingDown className="h-5 w-5 text-[#EA580C]" /></div>
              <div className="text-2xl font-extrabold text-[#EA580C] tabular-nums">{fmt(data.total_charges)}</div>
              <div className="text-xs text-slate-500 mt-1">{data.charges.length} compte(s) mouvementé(s)</div>
            </div>
            <div className={`${isProfit ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"} border-2 rounded-2xl p-5`} data-testid="result-card">
              <div className="flex items-center justify-between mb-2"><span className="text-xs uppercase tracking-wider text-slate-500 font-bold">Résultat net</span>{isProfit ? <TrendingUp className="h-5 w-5 text-[#166534]" /> : <TrendingDown className="h-5 w-5 text-red-600" />}</div>
              <div className={`text-2xl font-extrabold tabular-nums ${isProfit ? "text-[#166534]" : "text-red-600"}`}>{fmt(data.resultat_net)}</div>
              <div className="text-xs text-slate-500 mt-1">Marge : {data.marge_pct}%</div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Produits */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden" data-testid="produits-section">
              <div className="px-5 py-4 bg-green-50 border-b border-green-100">
                <h2 className="font-heading font-bold text-lg text-[#166534]">Produits (Classe 7)</h2>
              </div>
              <table className="w-full text-sm">
                <tbody>
                  {data.produits.map(p => (
                    <tr key={p.code} className="border-b border-slate-100">
                      <td className="px-4 py-2 font-mono text-xs text-slate-500 w-20">{p.code}</td>
                      <td className="px-4 py-2">{p.name}</td>
                      <td className="px-4 py-2 text-right tabular-nums font-semibold text-[#166534]">{fmt(p.amount)}</td>
                    </tr>
                  ))}
                  {data.produits.length === 0 && <tr><td colSpan={3} className="px-4 py-6 text-center text-slate-400">Aucun produit enregistré.</td></tr>}
                  <tr className="bg-green-50 font-bold">
                    <td colSpan={2} className="px-4 py-3">TOTAL PRODUITS</td>
                    <td className="px-4 py-3 text-right tabular-nums text-[#166534]">{fmt(data.total_produits)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Charges */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden" data-testid="charges-section">
              <div className="px-5 py-4 bg-orange-50 border-b border-orange-100">
                <h2 className="font-heading font-bold text-lg text-[#EA580C]">Charges (Classe 6)</h2>
              </div>
              <table className="w-full text-sm">
                <tbody>
                  {data.charges.map(c => (
                    <tr key={c.code} className="border-b border-slate-100">
                      <td className="px-4 py-2 font-mono text-xs text-slate-500 w-20">{c.code}</td>
                      <td className="px-4 py-2">{c.name}</td>
                      <td className="px-4 py-2 text-right tabular-nums font-semibold text-[#EA580C]">{fmt(c.amount)}</td>
                    </tr>
                  ))}
                  {data.charges.length === 0 && <tr><td colSpan={3} className="px-4 py-6 text-center text-slate-400">Aucune charge enregistrée.</td></tr>}
                  <tr className="bg-orange-50 font-bold">
                    <td colSpan={2} className="px-4 py-3">TOTAL CHARGES</td>
                    <td className="px-4 py-3 text-right tabular-nums text-[#EA580C]">{fmt(data.total_charges)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
